if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton =
          this.querySelector('[type="submit"]');

        this.submitButtonText =
          this.submitButton?.querySelector('span');

        this.loadingSpinner =
          this.querySelector('.loading__spinner');

        this.hideErrors =
          this.dataset.hideErrors === 'true';

        this.isSubmitting = false;
        this.error = false;
        this.keepDisabledAfterError = false;

        /*
         * Product form may theoretically exist
         * without all expected children.
         *
         * Avoid breaking the entire page if
         * markup is incomplete.
         */
        if (!this.form || !this.submitButton) {
          return;
        }

        const variantIdInput =
          this.variantIdInput;

        /*
         * Shopify may render the variant ID input
         * disabled before JS initializes.
         *
         * It must be enabled so FormData includes it.
         */
        if (variantIdInput) {
          variantIdInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        if (
          document.querySelector('cart-drawer')
        ) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        /*
         * Prevent duplicate Add to Cart requests.
         */
        if (
          this.isSubmitting ||
          !this.form ||
          !this.submitButton ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        const variantId =
          this.variantIdInput?.value;

        if (!variantId) {
          this.handleErrorMessage(
            this.getGenericErrorMessage()
          );

          return;
        }

        this.error = false;
        this.keepDisabledAfterError = false;
        this.isSubmitting = true;

        this.handleErrorMessage();

        this.setLoadingState(true);

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                },
              };

        config.headers =
          config.headers || {};

        config.headers[
          'X-Requested-With'
        ] = 'XMLHttpRequest';

        /*
         * Browser must set multipart boundary
         * automatically for FormData.
         */
        delete config.headers[
          'Content-Type'
        ];

        const formData =
          new FormData(this.form);

        if (this.cart) {
          const sections =
            typeof this.cart
              .getSectionsToRender ===
            'function'
              ? this.cart
                  .getSectionsToRender()
                  .map(
                    (section) =>
                      section.id
                  )
                  .filter(Boolean)
              : [];

          if (sections.length) {
            formData.append(
              'sections',
              sections.join(',')
            );

            formData.append(
              'sections_url',
              window.location.pathname
            );
          }

          if (
            typeof this.cart
              .setActiveElement ===
            'function'
          ) {
            this.cart.setActiveElement(
              document.activeElement
            );
          }
        }

        config.body = formData;

        const cartAddUrl =
          this.getCartAddUrl();

        try {
          const httpResponse =
            await fetch(
              cartAddUrl,
              config
            );

          let response;

          try {
            response =
              await httpResponse.json();
          } catch (parseError) {
            throw new Error(
              `Cart API returned an invalid response (HTTP ${httpResponse.status}).`
            );
          }

          /*
           * Shopify usually returns JSON for
           * validation/inventory errors.
           *
           * Handle both HTTP failures and
           * Shopify response.status failures.
           */
          if (
            !httpResponse.ok ||
            response?.status
          ) {
            this.handleCartError(
              response,
              formData,
              httpResponse.status
            );

            return;
          }

          /*
           * Successful Add to Cart.
           */
          this.error = false;

          if (
            typeof publish ===
              'function' &&
            typeof PUB_SUB_EVENTS !==
              'undefined' &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(
              PUB_SUB_EVENTS.cartUpdate,
              {
                source:
                  'product-form',

                productVariantId:
                  formData.get('id'),

                cartData: response,
              }
            );
          }

          /*
           * No Cart Drawer / Notification:
           * fall back to Shopify Cart page.
           */
          if (!this.cart) {
            window.location.assign(
              this.getCartUrl()
            );

            return;
          }

          const quickAddModal =
            this.closest(
              'quick-add-modal'
            );

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.renderCartContents(
                    response
                  );
                });
              },
              {
                once: true,
              }
            );

            if (
              typeof quickAddModal.hide ===
              'function'
            ) {
              quickAddModal.hide(true);
            } else {
              this.renderCartContents(
                response
              );
            }
          } else {
            this.renderCartContents(
              response
            );
          }
        } catch (error) {
          /*
           * Network, parsing, or unexpected
           * Cart API failure.
           */
          this.error = true;

          const message =
            this.getGenericErrorMessage();

          this.handleErrorMessage(
            message
          );

          if (
            typeof publish ===
              'function' &&
            typeof PUB_SUB_EVENTS !==
              'undefined' &&
            PUB_SUB_EVENTS.cartError
          ) {
            publish(
              PUB_SUB_EVENTS.cartError,
              {
                source:
                  'product-form',

                productVariantId:
                  formData.get('id'),

                errors:
                  error?.message ||
                  message,

                message,
              }
            );
          }

          console.error(
            '[Product Form]',
            error
          );
        } finally {
          this.isSubmitting = false;

          this.setLoadingState(false);

          /*
           * Only keep disabled when Shopify has
           * genuinely reported an unavailable /
           * sold-out variant.
           */
          if (
            !this.keepDisabledAfterError
          ) {
            this.submitButton?.removeAttribute(
              'aria-disabled'
            );
          }
        }
      }

      handleCartError(
        response,
        formData,
        httpStatus
      ) {
        this.error = true;

        const errorMessage =
          this.getResponseErrorMessage(
            response
          );

        if (
          typeof publish ===
            'function' &&
          typeof PUB_SUB_EVENTS !==
            'undefined' &&
          PUB_SUB_EVENTS.cartError
        ) {
          publish(
            PUB_SUB_EVENTS.cartError,
            {
              source:
                'product-form',

              productVariantId:
                formData.get('id'),

              errors:
                response?.errors ||
                response?.description ||
                errorMessage,

              message:
                response?.message ||
                errorMessage,

              status:
                response?.status ||
                httpStatus,
            }
          );
        }

        this.handleErrorMessage(
          errorMessage
        );

        /*
         * Do NOT convert every Shopify error
         * into "Sold Out".
         *
         * Quantity limits, validation issues,
         * app errors, etc. should remain normal
         * error messages.
         */
        const isSoldOut =
          this.isSoldOutError(
            errorMessage
          );

        if (!isSoldOut) {
          return;
        }

        const soldOutMessage =
          this.submitButton?.querySelector(
            '.sold-out-message'
          );

        if (!soldOutMessage) {
          return;
        }

        this.keepDisabledAfterError = true;

        this.submitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        if (this.submitButtonText) {
          this.submitButtonText.classList.add(
            'hidden'
          );
        }

        soldOutMessage.classList.remove(
          'hidden'
        );
      }

      getResponseErrorMessage(
        response
      ) {
        if (
          typeof response?.description ===
            'string' &&
          response.description.trim()
        ) {
          return response.description;
        }

        if (
          typeof response?.message ===
            'string' &&
          response.message.trim()
        ) {
          return response.message;
        }

        if (
          typeof response?.errors ===
          'string'
        ) {
          return response.errors;
        }

        if (
          response?.errors &&
          typeof response.errors ===
            'object'
        ) {
          const values =
            Object.values(
              response.errors
            )
              .flat()
              .filter(Boolean);

          if (values.length) {
            return values.join(' ');
          }
        }

        return this.getGenericErrorMessage();
      }

      getGenericErrorMessage() {
        return (
          window.cartStrings?.error ||
          'There was an error adding this item to your cart. Please try again.'
        );
      }

      isSoldOutError(message) {
        if (
          typeof message !== 'string'
        ) {
          return false;
        }

        return /(sold out|out of stock|not available)/i.test(
          message
        );
      }

      renderCartContents(response) {
        if (
          !this.cart ||
          typeof this.cart
            .renderContents !==
            'function'
        ) {
          /*
           * Product was already added successfully.
           * If drawer rendering is unavailable,
           * simply send the customer to Cart.
           */
          window.location.assign(
            this.getCartUrl()
          );

          return;
        }

        try {
          this.cart.renderContents(
            response
          );

          if (
            this.cart.classList.contains(
              'is-empty'
            )
          ) {
            this.cart.classList.remove(
              'is-empty'
            );
          }
        } catch (error) {
          /*
           * The Add to Cart itself succeeded.
           * Do not incorrectly tell the customer
           * that adding failed just because the
           * drawer UI could not refresh.
           */
          console.error(
            '[Product Form] Cart UI render failed:',
            error
          );

          window.location.assign(
            this.getCartUrl()
          );
        }
      }

      setLoadingState(isLoading) {
        if (!this.submitButton) {
          return;
        }

        this.submitButton.classList.toggle(
          'loading',
          isLoading
        );

        if (isLoading) {
          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );
        } else if (
          !this.keepDisabledAfterError
        ) {
          this.submitButton.removeAttribute(
            'aria-disabled'
          );
        }

        if (this.loadingSpinner) {
          this.loadingSpinner.classList.toggle(
            'hidden',
            !isLoading
          );
        }
      }

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (
          !this.errorMessageWrapper
        ) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (!this.submitButton) {
          return;
        }

        const soldOutMessage =
          this.submitButton.querySelector(
            '.sold-out-message'
          );

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent =
              text;
          }

          return;
        }

        this.submitButton.removeAttribute(
          'disabled'
        );

        this.submitButton.removeAttribute(
          'aria-disabled'
        );

        this.keepDisabledAfterError = false;

        if (this.submitButtonText) {
          this.submitButtonText.classList.remove(
            'hidden'
          );

          this.submitButtonText.textContent =
            window.variantStrings
              ?.addToCart ||
            'Add to cart';
        }

        if (soldOutMessage) {
          soldOutMessage.classList.add(
            'hidden'
          );
        }
      }

      getCartAddUrl() {
        if (
          window.routes
            ?.cart_add_url
        ) {
          return window.routes
            .cart_add_url;
        }

        if (
          typeof routes !==
            'undefined' &&
          routes.cart_add_url
        ) {
          return routes.cart_add_url;
        }

        return '/cart/add.js';
      }

      getCartUrl() {
        if (
          window.routes?.cart_url
        ) {
          return window.routes
            .cart_url;
        }

        if (
          typeof routes !==
            'undefined' &&
          routes.cart_url
        ) {
          return routes.cart_url;
        }

        return '/cart';
      }

      get variantIdInput() {
        return (
          this.form?.querySelector(
            '[name="id"]'
          ) || null
        );
      }
    }
  );
}